import { useEffect, useMemo, useState, useCallback } from 'react';
import { EditableBadge, EditableBadgeValueProps } from 'framework/ui/ui-builder';
import {
  RECRUITING_VACANCY_GROUP_CLOSE_VACANCY_GROUP,
  VACANCY_GROUP_STATUS,
  VACANCY_GROUP_STATUS_OPTIONS,
  useVacancyGroupService,
} from 'domain/shared/newVacancy';
import { RECRUITING_CONTEXT } from 'domain/recruiting/RecruitingContext';
import { useAuthorization } from 'framework/authorization';
import { Badge } from 'framework/ui/mui-builder';
import { useRecruitmentApi } from 'infrastructure/api';
import useVacancyPublicationService from 'domain/recruiting/newVacancy/show/publications/vacancyPublicationService';
import { InputLoader } from 'framework/ui/mui-builder';
import { useVacancyContext } from 'domain/recruiting/newVacancy/VacancyContext';
import { useFormShowContext } from 'framework/form';
import { useShowContext } from 'framework/show';

const VacancyGroupStatusColor = {
  [VACANCY_GROUP_STATUS.REQUIRES_REVIEW]: 'warning' as const,
  [VACANCY_GROUP_STATUS.OPEN]: 'success' as const,
  [VACANCY_GROUP_STATUS.CLOSED]: 'warning' as const,
};

const VACANCY_GROUP_STATUS_CONFIG = {
  [VACANCY_GROUP_STATUS.REQUIRES_REVIEW]: {
    bgcolor: 'var(--mui-palette-background-warning)' as const,
    menuOptions: [],
  },
  [VACANCY_GROUP_STATUS.OPEN]: {
    bgcolor: 'var(--mui-palette-background-success)' as const,
    menuOptions: [VACANCY_GROUP_STATUS.CLOSED],
  },
  [VACANCY_GROUP_STATUS.CLOSED]: {
    bgcolor: 'var(--mui-palette-background-warning)' as const,
    menuOptions: [],
  },
};

interface StatusCheckResults {
  hasActiveVacancies: boolean;
  hasActivePublications: boolean;
  hasActiveApplications: boolean;
}

const VacancyGroupStatusBadge = () => {
  const { setData, data } = useShowContext();
  const { isEditMode } = useFormShowContext();
  const { isGranted } = useAuthorization();
  const { areVacanciesUpdated, arePublicationsUpdated } = useVacancyContext();

  const showData = data.getData();
  const { id, alias, details: { status = '' } = {} } = showData;

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [statusChecks, setStatusChecks] = useState<StatusCheckResults>({
    hasActiveVacancies: true,
    hasActivePublications: true,
    hasActiveApplications: true,
  });

  const { sendPostRequest } = useRecruitmentApi();
  const { searchWithFilters } = useVacancyPublicationService();
  const { closeVacancyGroup, searchVacancy } = useVacancyGroupService();

  const checkActiveVacancies = useCallback(async (): Promise<boolean> => {
    const vacancies = await searchVacancy({
      filter: {
        'vacancyGroup.id': { eq: id },
        status: { eq: 'OPEN' },
      },
      pageSize: 1,
    });
    return vacancies.totalElements > 0;
  }, [id, searchVacancy]);

  const checkActivePublications = useCallback(async (): Promise<boolean> => {
    const publications = await searchWithFilters({
      filter: {
        'vacancyGroup.id': { eq: id },
        status: { eq: 'OPEN' },
      },
      pageSize: 1,
    });
    return publications.totalElements > 0;
  }, [id, searchWithFilters]);

  const checkActiveApplications = useCallback(async (): Promise<boolean> => {
    const response = await sendPostRequest('/candidates/applications/search', {
      filter: {
        vacancyGroupAlias: { in: [alias] },
        currentPipelineStage: { not_in: ['Rejected', 'Closed', 'Hired'] },
      },
      pageSize: 1,
    });
    const result = await response.json();
    return result.totalElements > 0;
  }, [alias, sendPostRequest]);

  const loadStatusChecks = useCallback(async (): Promise<StatusCheckResults> => {
    const [hasActiveVacancies, hasActivePublications, hasActiveApplications] = await Promise.all([
      checkActiveVacancies(),
      checkActivePublications(),
      checkActiveApplications(),
    ]);

    return { hasActiveVacancies, hasActivePublications, hasActiveApplications };
  }, [checkActiveVacancies, checkActivePublications, checkActiveApplications]);

  const refreshStatusChecks = useCallback(async () => {
    setIsLoading(true);
    try {
      const results = await loadStatusChecks();
      setStatusChecks(results);
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  }, [loadStatusChecks]);

  useEffect(() => {
    refreshStatusChecks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isInitialLoading) {
      checkActiveVacancies().then(hasActiveVacancies => {
        setStatusChecks(prev => ({ ...prev, hasActiveVacancies }));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areVacanciesUpdated]);

  useEffect(() => {
    if (!isInitialLoading) {
      checkActivePublications().then(hasActivePublications => {
        setStatusChecks(prev => ({ ...prev, hasActivePublications }));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arePublicationsUpdated]);

  const handleStatusChange = useCallback(async () => {
    const vacancyGroup = await closeVacancyGroup(id);
    setData!({
      ...showData,
      details: {
        ...showData.details,
        status: vacancyGroup.details.status,
      },
      updatedAt: vacancyGroup.updatedAt,
      updatedBy: vacancyGroup.updatedBy,
    });
  }, [closeVacancyGroup, id, setData, showData]);

  const statusOptions = useMemo(() => {
    if (isLoading) return [];

    const currentStatusConfig = VACANCY_GROUP_STATUS_CONFIG[status];
    if (!currentStatusConfig || currentStatusConfig.menuOptions.length === 0) return [];

    return currentStatusConfig.menuOptions.map(option => {
      const optionConfig = VACANCY_GROUP_STATUS_CONFIG[option];
      const isClosing = option === VACANCY_GROUP_STATUS.CLOSED;

      const disabled =
        isClosing &&
        (statusChecks.hasActiveVacancies || statusChecks.hasActiveApplications || statusChecks.hasActivePublications);

      const disabledReason = disabled ? (
        <ul>
          {statusChecks.hasActiveVacancies && <li>The group cannot be closed if it contains active vacancies.</li>}
          {statusChecks.hasActivePublications && <li>The group cannot be closed if it has active publications.</li>}
          {statusChecks.hasActiveApplications && <li>The group cannot be closed if it has active applications.</li>}
        </ul>
      ) : undefined;

      return {
        ...optionConfig,
        value: option,
        disabled,
        disabledReason,
      };
    });
  }, [status, isLoading, statusChecks]);

  const canUpdateStatus = isGranted(RECRUITING_VACANCY_GROUP_CLOSE_VACANCY_GROUP, RECRUITING_CONTEXT);

  if (isLoading) {
    return <InputLoader />;
  }

  if (!statusOptions.length || isEditMode) {
    return (
      <Badge color={VacancyGroupStatusColor[status]} sx={{ padding: '4px', display: 'flex' }}>
        {VACANCY_GROUP_STATUS_OPTIONS[status]}
      </Badge>
    );
  }

  return (
    <EditableBadge
      color={VacancyGroupStatusColor[status]}
      values={statusOptions as EditableBadgeValueProps[]}
      onChange={handleStatusChange}
      canUpdate={canUpdateStatus}
      badgeSx={{ padding: '4px', border: '1px solid', borderColor: 'border.contrast', display: 'flex' }}
      menuItemSx={{
        margin: '4px',
        borderRadius: '4px',
      }}
    >
      {VACANCY_GROUP_STATUS_OPTIONS[status]}
    </EditableBadge>
  );
};

export default VacancyGroupStatusBadge;
